/**
 * How a task list is drawn: the lines on show, each top-level line with its
 * subtasks under it, and the fold of lines done on an earlier day. One answer
 * for the phone's `TaskList` and the desk's Lists, which had drifted — the
 * desk dropped old done lines with no fold to find them in (UI-audit a3-14).
 *
 * The rule both had wrong: a subtask was drawn only under a parent that was
 * itself on show, so an *open* subtask of a parent ticked yesterday was in no
 * list at all while still counted as open. Here a line is a child only when
 * its parent is a top-level line on show; anything else is top-level. An
 * open line is never hidden.
 *
 * Pure and import-free so the Worker's suite can test it.
 */
export interface TaskLine {
  id: string
  parent_id: string | null
  position: number
}

export interface TaskFold<T> {
  /** Top-level lines on show, by position, each with its subtasks on show. */
  top: { item: T; children: T[] }[]
  /** Lines folded away under "Done", by position. */
  done: T[]
}

const byPosition = (a: TaskLine, b: TaskLine) => a.position - b.position

export function foldTasks<T extends TaskLine>(items: readonly T[], folded: (i: T) => boolean): TaskFold<T> {
  const shown = items.filter(i => !folded(i)).sort(byPosition)
  const done = items.filter(folded).sort(byPosition)
  const byId = new Map(shown.map(i => [i.id, i]))
  const hasShownParent = (i: T) => !!i.parent_id && byId.has(i.parent_id)
  // A child only under a parent that is itself top-level: one level, so a
  // line whose shown parent is somebody's child stands on its own rather
  // than vanishing a level down.
  const isChild = (i: T) => hasShownParent(i) && !hasShownParent(byId.get(i.parent_id as string) as T)
  return {
    top: shown.filter(i => !isChild(i)).map(item => ({
      item,
      children: shown.filter(c => c.parent_id === item.id && isChild(c)),
    })),
    done,
  }
}
